import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddGlucose } from 'src/app/interfaces/Glucose/IAddGlucose';
import { GlucoseService } from 'src/app/services/glucose.service';
import { dateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-glucose-add-edit',
  standalone: true,
  templateUrl: './glucose-add-edit.component.html',
  styleUrls: ['./glucose-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
  ],
})
export class GlucoseAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;

  isEdit = false;
  editGlucoseId?: number;

  glucoseSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  glucoseForm: FormGroup = new FormGroup({});

  constructor(
    private readonly glucoseService: GlucoseService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.isLoading = true;
    this.isDropdownLoading = true;

    this.glucoseForm = this.formBuilder.group({
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      glucoseValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(600),
        ]),
      ],
    });

    this.glucoseService.glucoseEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.isLoading = false;

          this.editGlucoseId = data?.id;

          this.glucoseForm.patchValue({
            dateValue:
              data?.onSetDateTime && this.convertToLocale(data?.onSetDateTime),
            glucoseValue: data?.glucoseValue,
          });
        } else {
          this.isEdit = false;
          this.isLoading = false;

          this.editGlucoseId = undefined;

          this?.glucoseForm.reset();

          this.glucoseForm.patchValue({
            dateValue: this.convertToLocale(),
          });
        }
      });

    this.glucoseService.glucoseModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.glucoseForm.reset();

    this.glucoseService.glucoseModalSub.next(false);

    this.glucoseSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.glucoseSubmitted = true;

    if (this.glucoseForm.valid) {
      const glucoseObj: IAddGlucose = {};

      glucoseObj.id = this.editGlucoseId;

      glucoseObj.onSetDateTime = moment(
        this.glucoseForm.value?.dateValue
      ).format();

      glucoseObj.glucoseValue = this.glucoseForm.get('glucoseValue')?.value;

      if (this.isEdit) {
        this.glucoseService.editGlucose(glucoseObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Glucose record edited successfully'
            );
            this.glucoseService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit Glucose record');

            this.handleCancel();
          },
        });
      } else {
        this.glucoseService.addGlucose(glucoseObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Glucose record added successfully'
            );
            this.glucoseService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Glucose record');
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  getCurrentDateTime() {
    return new Date().toISOString().slice(0, 16);
  }

  // CONVERT UTC TO LOCAL
  convertToLocale(date?: string) {
    const currentDateTime = date ? moment(date).format() : moment().format();

    const isoDate = currentDateTime.slice(0, 16);

    return isoDate;
  }
}
